<template>
  <v-dialog v-model="open" max-width="540" persistent>
    <v-card>
      <v-card-title class="d-block primary text-white">
        <span class="text-h6">Share "{{ groupName }}"</span>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Not a pro subscriber -->
        <v-alert v-if="!user.isProSubscriber && !user.isEnterpriseSubscriber" type="info" variant="tonal" class="mb-0">
          Sharing groups requires a Pro subscription.
          <div class="mt-2">
            <v-btn size="small" color="primary" href="/subscribe">Upgrade</v-btn>
          </div>
        </v-alert>

        <template v-else>
          <!-- Loading -->
          <v-progress-linear v-if="loading" indeterminate class="mb-4" />

          <template v-if="!loading">
            <!-- Current shares -->
            <p v-if="shares.length === 0" class="text-body-2 text-medium-emphasis mb-4">
              This {{ groupLabel }} is not currently shared with anyone.
            </p>

            <v-table v-else density="compact" class="mb-4">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Can Edit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="share in shares" :key="share.id">
                  <td>{{ share.email }}</td>
                  <td>
                    <v-switch
                      v-model="share.can_edit"
                      hide-details
                      density="compact"
                      color="primary"
                      :loading="share.saving"
                      @change="updateShare(share)"
                    />
                  </td>
                  <td>
                    <v-btn
                      :icon="mdiDelete"
                      size="small"
                      variant="plain"
                      color="error"
                      :loading="share.removing"
                      @click="removeShare(share)"
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>

            <!-- Add new share -->
            <v-divider class="mb-4" />
            <p class="text-subtitle-2 mb-3">Add a user by email:</p>
            <v-row density="compact" align="center" no-gutters class="gap-2">
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="emailInput"
                  label="Email address"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :error="lookupError !== null"
                  :error-messages="lookupError"
                  @keydown.enter="lookupUser"
                />
              </v-col>
              <v-col cols="auto" class="ml-2">
                <v-btn
                  color="primary"
                  size="small"
                  variant="outlined"
                  :loading="looking"
                  :disabled="!emailInput.trim()"
                  @click="lookupUser"
                >
                  Look up
                </v-btn>
              </v-col>
            </v-row>

            <!-- Found user — confirm add -->
            <v-card v-if="foundUser" variant="tonal" color="primary" class="mt-3 pa-3">
              <div class="d-flex align-center justify-space-between">
                <span class="text-body-2">{{ foundUser.email }}</span>
                <div class="d-flex align-center gap-2">
                  <v-checkbox
                    v-model="newShare.canEdit"
                    label="Can edit"
                    density="compact"
                    hide-details
                    class="mr-2"
                  />
                  <v-btn
                    color="primary"
                    size="small"
                    :loading="adding"
                    @click="addShare"
                  >
                    Add
                  </v-btn>
                </div>
              </div>
            </v-card>
          </template>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/store/api'
import { useAppStore, useUserStore } from '@/store'
import { mapStoreState } from '@/store/helpers'
import { mdiDelete } from '@mdi/js'

export default {
  props: {
    modelValue: { type: Boolean, default: false },
    groupId: { type: Number, default: null },
    groupName: { type: String, default: '' },
    groupType: { type: String, default: 'filer_group' }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      mdiDelete,
      loading: false,
      looking: false,
      adding: false,
      shares: [],
      emailInput: '',
      foundUser: null,
      lookupError: null,
      newShare: { canEdit: false }
    }
  },

  computed: {
    ...mapStoreState({ user: state => state.user }),
    open: {
      get() { return this.modelValue },
      set(v) { this.$emit('update:modelValue', v) }
    },
    groupLabel() {
      return this.groupType === 'stock_group' ? 'watchlist' : 'fund group'
    },
    sharesEndpoint() {
      const base = this.groupType === 'stock_group' ? 'watchlists' : 'groups'
      return `${base}/${this.groupId}/shares`
    },
    alreadySharedIds() {
      return this.shares.map(s => s.user_id)
    }
  },

  watch: {
    modelValue(val) {
      if (val && this.groupId) this.loadShares()
    },
    emailInput() {
      this.foundUser = null
      this.lookupError = null
    }
  },

  methods: {
    async loadShares() {
      this.loading = true
      try {
        const res = await api.get(this.sharesEndpoint)
        this.shares = res.data.map(s => ({ ...s, saving: false, removing: false }))
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: 'Failed to load shares' })
      } finally {
        this.loading = false
      }
    },

    async lookupUser() {
      const email = this.emailInput.trim()
      if (!email) return
      this.looking = true
      this.foundUser = null
      this.lookupError = null
      try {
        const res = await api.get('user_lookup', { params: { email } })
        if (this.alreadySharedIds.includes(res.data.id)) {
          this.lookupError = 'This user already has access to this group'
        } else {
          this.foundUser = res.data
        }
      } catch (e) {
        this.lookupError = e.response?.status === 404
          ? 'No WisdomWhale account found with that email address'
          : (e.response?.data || e.message)
      } finally {
        this.looking = false
      }
    },

    async addShare() {
      if (!this.foundUser) return
      this.adding = true
      try {
        const res = await api.post(this.sharesEndpoint, {
          user_id: this.foundUser.id,
          can_edit: this.newShare.canEdit
        })
        this.shares.push({ ...res.data, email: this.foundUser.email, saving: false, removing: false })
        this.emailInput = ''
        this.foundUser = null
        this.newShare = { canEdit: false }
      } catch (e) {
        const msg = e.response?.data?.[0] || e.message
        useAppStore().showNotification({ type: 'error', text: msg })
      } finally {
        this.adding = false
      }
    },

    async updateShare(share) {
      share.saving = true
      try {
        await api.post(`${this.sharesEndpoint}/${share.id}`, { can_edit: share.can_edit })
      } catch (e) {
        share.can_edit = !share.can_edit
        useAppStore().showNotification({ type: 'error', text: e.message })
      } finally {
        share.saving = false
      }
    },

    async removeShare(share) {
      share.removing = true
      try {
        await api.delete(`${this.sharesEndpoint}/${share.id}`)
        this.shares = this.shares.filter(s => s.id !== share.id)
      } catch (e) {
        useAppStore().showNotification({ type: 'error', text: e.message })
        share.removing = false
      }
    },

    close() {
      this.shares = []
      this.emailInput = ''
      this.foundUser = null
      this.lookupError = null
      this.newShare = { canEdit: false }
      this.open = false
    }
  }
}
</script>
